import * as z from "zod";
import { ChargebacksAvailabilitySchema } from "./chargebacks/chargebacks-config";
import { ONBOARDING_V2_STATUSES, PRODUCT_IDS } from "./initialize";
import { OrderEditingAvailabilitySchema } from "./order-editing/order-editing-config";
import { RecoverAvailabilitySchema } from "./recover/recover-config";
import { ReturnsAvailabilitySchema } from "./returns/returns-config";
import { ShippingFulfillmentAvailabilitySchema } from "./shipping-fulfillment/shipping-fulfillment-config";

export const AdminConfigSchema = z.object({
  skipPricing: z.boolean(),
  billingRequired: z.boolean(),
  /** When false, completeOnboarding will not set settings.returns.enabled=true. Defaults to true. */
  autoPush: z.boolean(),
});
export type AdminConfig = z.infer<typeof AdminConfigSchema>;

/**
 * Team-level deal sizing data computed by the onboarding data pull Temporal
 * workflow. `totalAnnualOrderVolume` is `null` while the workflow is still
 * running or when it couldn't compute a value (e.g. no Shopify integration).
 * Used by the returns onboarding flow to branch large merchants — in any
 * dimension, web or non-web, US or international — to a human-assisted
 * handoff path so they can be routed to the right Returns SKU on the call.
 */
export const DealSizingSchema = z.object({
  totalAnnualOrderVolume: z.number().nullable(),
  provisionalOrderCount30d: z.number().nullable(),
});
export type DealSizing = z.infer<typeof DealSizingSchema>;

export const AskRedoThreadRefSchema = z.object({
  threadId: z.string(),
  stepId: z.string(),
  stepLabel: z.string(),
  startedAt: z.string(),
  lastUpdatedAt: z.string(),
});
export type AskRedoThreadRef = z.infer<typeof AskRedoThreadRefSchema>;

export const getProgress = {
  name: "getProgress" as const,
  input: z.object({ productId: z.enum(PRODUCT_IDS) }),
  output: z.object({
    progress: z
      .object({
        currentStepId: z.string(),
        formData: z.record(z.string(), z.record(z.string(), z.unknown())),
        status: z.enum(ONBOARDING_V2_STATUSES),
        version: z.number(),
        dealSize: z.number().nullable(),
        /** Most recent Ask Redo thread id (for chat session restoration). */
        activeAskRedoThreadId: z.string().nullable(),
      })
      .nullable(),
    productConfig: z
      .object({
        chargebacks: ChargebacksAvailabilitySchema,
        orderEditing: OrderEditingAvailabilitySchema,
        recover: RecoverAvailabilitySchema,
        returns: ReturnsAvailabilitySchema,
        shippingFulfillment: ShippingFulfillmentAvailabilitySchema,
      })
      .partial()
      .optional(),
    adminConfig: AdminConfigSchema.optional(),
    dealSizing: DealSizingSchema.optional(),
  }),
};
